import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';
import { appEnv } from '../../../app.config';

export type PermissionKey =
  | 'messaging.view'
  | 'messaging.compose'
  | 'messaging.sms.create'
  | 'messaging.reply'
  | 'messaging.templates.manage'
  | 'messaging.settings.manage';

export interface PermissionDef {
  key: PermissionKey;
  label: string;
}

export interface RoleGrantRow {
  roleId: number;
  roleName: string;
  locked: boolean;
  grants: Record<PermissionKey, boolean>;
}

export interface PermissionMatrix {
  permissions: PermissionDef[];
  roles: RoleGrantRow[];
}

export interface UserOverrides {
  permissions: PermissionDef[];
  roleName: string | null;
  roleGrants: Record<PermissionKey, boolean>;
  userGrants: Record<PermissionKey, boolean>;
}

@Injectable({
  providedIn: 'root'
})
export class MessagingPermissionsService {
  private mine$: Observable<Record<PermissionKey, boolean>> | null = null;

  constructor(private httpClient: HttpClient) {}

  /** Cached per session - every component gating on "can I do this" shares one HTTP call. Call refresh() after a grant that might affect the current user. */
  getMine(): Observable<Record<PermissionKey, boolean>> {
    if (!this.mine$) {
      this.mine$ = this.httpClient
        .get<{ permissions: Record<PermissionKey, boolean> }>(`${appEnv.api}crm/messaging/permissions/mine`)
        .pipe(
          map((res) => res.permissions),
          shareReplay(1)
        );
    }

    return this.mine$;
  }

  refresh(): void {
    this.mine$ = null;
  }

  getMatrix(): Observable<PermissionMatrix> {
    return this.httpClient.get<PermissionMatrix>(`${appEnv.api}crm/messaging/permissions`);
  }

  updateRolePermission(roleId: number, permissionKey: PermissionKey, enabled: boolean): Observable<PermissionMatrix> {
    return this.httpClient.put<PermissionMatrix>(`${appEnv.api}crm/messaging/permissions/role`, {
      role_id: roleId,
      permission_key: permissionKey,
      enabled
    });
  }

  getUserOverrides(userId: number): Observable<UserOverrides> {
    return this.httpClient.get<UserOverrides>(`${appEnv.api}crm/messaging/permissions/users/${userId}`);
  }

  updateUserPermission(userId: number, permissionKey: PermissionKey, enabled: boolean): Observable<UserOverrides> {
    return this.httpClient.put<UserOverrides>(`${appEnv.api}crm/messaging/permissions/users/${userId}`, {
      permission_key: permissionKey,
      enabled
    });
  }
}
